import * as React from "react";
import {FC, useCallback, useMemo} from "react";
import {Alert} from "@mui/material";
import {DateFormat} from "../../../utility/DateFormat";
import Grid from "@mui/material/Grid";
import {
   EnumOrder2FulState,
   EnumOrder2OrderState,
   EnumOrder2PayState,
   EnumOrder2TrackingOrderState,
   EnumOrder2TrackingReturnState,
   FilterFindManyOrder2Input,
   Order2Item,
   OrderFragment,
   SortFindManyOrder2Input,
   useOrdersListLazyQuery,
   useOrdersSelectAllLazyQuery
} from "../../../Queries";
import {FlexListColumn} from "../../../components/flexlist/FlexTypes";
import {RangeList} from "../../../components/rangelist/RangeList";
import {RangeCommand, RangeFilter, RangeSort, RangeTab} from "../../../components/rangelist/RangeListTypes";
import {FileDownload} from "@mui/icons-material";
import {OrdersExport} from "./OrdersExport";
import {Thumbnails} from "../../../components/Thumbnails";

type OrdersPanelProps = {
   listId: string
   brandHouseId?: string
   shopId?: string
   consumerId?: string
   columns?: string[]
   maxRows?: number
   hideHeader?: boolean,
   onSelectOrder?: (order: OrderFragment, newWindow?: boolean) => void
   extraCommands?: RangeCommand<OrderFragment>[]
   viewId?: string
}

const PAGE_SIZE = 30;

export const OrdersList: FC<OrdersPanelProps> = ({
                                                    listId,
                                                    shopId,
                                                    brandHouseId,
                                                    consumerId,
                                                    columns,
                                                    maxRows,
                                                    hideHeader,
                                                    onSelectOrder,
                                                    extraCommands,
                                                    viewId
                                                 }) => {
   const [exportIds, setExportIds] = React.useState<string[]>([])
   const [fetchMore] = useOrdersListLazyQuery();
   const [fetchAllIds] = useOrdersSelectAllLazyQuery();

   const sortSpec: RangeSort<SortFindManyOrder2Input>[] = [{
      id: 'CreatedAsc',
      label: 'Created (oldest first)',
      value: SortFindManyOrder2Input._ID_ASC
   }, {
      id: 'CreatedDesc',
      label: 'Created (newest first)',
      value: SortFindManyOrder2Input._ID_DESC
   }, {
      id: 'returncanceldesc',
      label: 'Recent Returns/Cancels',
      value: SortFindManyOrder2Input.UPDATEDLINETS_DESC
   }]

   const filterSpec: RangeFilter<FilterFindManyOrder2Input>[] = [{
      id: 'fulState',
      label: 'Fulfillment',
      options: [{
         label: 'Fulfilled',
         filter: {
            fulState: EnumOrder2FulState.FULFILLED
         }
      }, {
         label: 'Unfulfilled',
         filter: {
            fulState: EnumOrder2FulState.UNFULFILLED,
         }
      }, {
         label: 'Other',
         filter: {
            OR: [{
               fulState: EnumOrder2FulState.OPEN
            }, {
               fulState: EnumOrder2FulState.ON_HOLD
            }, {
               fulState: EnumOrder2FulState.IN_PROGRESS
            }, {
               fulState: EnumOrder2FulState.PARTIALLY_FULFILLED
            }, {
               fulState: EnumOrder2FulState.PENDING_FULFILLMENT
            }, {
               fulState: EnumOrder2FulState.SCHEDULED
            }, {
               fulState: null
            }]
         }
      }]
   }, {
      id: 'payState',
      label: 'Capture',
      options: [{
         label: 'Paid',
         filter: {
            payState: EnumOrder2PayState.PAID
         }
      }, {
         label: 'Unpaid',
         filter: {
            payState: EnumOrder2PayState.UNPAID
         }
      }, {
         label: 'Pending',
         filter: {
            payState: EnumOrder2PayState.PENDING
         }
      }, {
         label: 'Refunded',
         filter: {
            payState: EnumOrder2PayState.REFUNDED
         }
      }, {
         label: 'Other',
         filter: {
            payState: EnumOrder2PayState.OTHER
         }
      }]
   }, {
      id: 'orderState',
      label: 'Order state',
      options: [{
         label: 'Open',
         filter: {
            orderState: EnumOrder2OrderState.OPEN
         }
      }, {
         label: 'Completed',
         filter: {
            orderState: EnumOrder2OrderState.COMPLETED,
         }
      }, {
         label: 'Cancelled',
         filter: {
            orderState: EnumOrder2OrderState.CANCELLED,

         }
      }, {
         label: 'Partial',
         filter: {
            orderState: EnumOrder2OrderState.PARTIAL,
         }
      }]
   }, {
      id: 'delivery',
      label: 'Shipping status',
      options: [{
         label: 'Not sent',
         filter: {
            trackingOrderState: EnumOrder2TrackingOrderState.NOTSENT
         }
      }, {
         label: 'Transit',
         filter: {
            trackingOrderState: EnumOrder2TrackingOrderState.TRANSIT
         }
      }, {
         label: 'Received',
         filter: {
            trackingOrderState: EnumOrder2TrackingOrderState.RECEIVED,
         }
      }, {
         label: 'Awaiting pickup',
         filter: {
            trackingOrderState: EnumOrder2TrackingOrderState.PICKUP_AVAILABLE,
         }
      }, {
         label: 'Error',
         filter: {
            trackingOrderState: EnumOrder2TrackingOrderState.ERROR,
         }
      }]
   }, {
      id: 'return',
      label: 'Return status',
      options: [{
         label: 'Not sent',
         filter: {
            trackingReturnState: EnumOrder2TrackingReturnState.NOTSENT
         }
      }, {
         label: 'Transit',
         filter: {
            trackingReturnState: EnumOrder2TrackingReturnState.TRANSIT
         }
      }, {
         label: 'Received',
         filter: {
            trackingReturnState: EnumOrder2TrackingReturnState.RECEIVED,
         }
      }, {
         label: 'Awaiting pickup',
         filter: {
            trackingReturnState: EnumOrder2TrackingReturnState.PICKUP_AVAILABLE,
         }
      }, {
         label: 'Error',
         filter: {
            trackingReturnState: EnumOrder2TrackingReturnState.ERROR,
         }
      }]
   }, {
      id: 'proforma',
      label: 'Proforma order',
      options: [{
         label: 'Yes',
         filter: {
            proforma: true
         }
      }, {
         label: 'No',
         filter: {
            _operators: {
               proforma: {ne: true}
            }
         }
      }]
   }]

   // Columns to exclude from most tabs (only show on Shipment issues)
   const trackingColumnsExcluded = ['orderState', 'returnState'];

   const tabsSpec: RangeTab[] = useMemo(() => {
      if (viewId) {
         return [{
            id: 'all',
            label: 'All',
            filters: [],
            columns: columns,
            columnsExcluded: trackingColumnsExcluded
         }]
      }
      return [{
         id: 'all',
         label: 'All',
         filters: [],
         columns: columns,
         columnsExcluded: trackingColumnsExcluded
      }, {
         id: 'open',
         label: 'Open',
         showCount: true,
         filters: [{
            filterId: 'fulState',
            values: ['Unfulfilled', 'Other']
         }, {
            filterId: 'orderState',
            values: ['Open']
         }, {
            filterId: 'payState',
            values: ['Unpaid', 'Pending']
         }],
         columns: columns,
         columnsExcluded: trackingColumnsExcluded
      }, {
         id: 'unpaid',
         label: 'Unpaid',
         showCount: true,
         filters: [{
            filterId: 'payState',
            values: ['Unpaid']
         }, {
            filterId: 'orderState',
            values: ['Open', 'Completed', 'Partial']
         }],
         columns: columns,
         columnsExcluded: trackingColumnsExcluded
      }, {
         id: 'returnissues',
         label: 'Refund issues',
         filters: [{
            filterId: 'fulState',
            values: ['Fulfilled']
         }, {
            filterId: 'payState',
            values: ['Paid']
         }, {
            filterId: 'orderState',
            values: ['Completed']
         }, {
            filterId: 'return',
            values: ['Received']
         }],
         columns: columns,
         columnsExcluded: trackingColumnsExcluded
      }, {
         id: 'shipmentissues',
         label: 'Shipment issues',
         filters: [{
            filterId: 'delivery',
            values: ['Awaiting pickup']
         }],
         columns: columns
      }]
   }, [columns, viewId]);


   const imagesColumn: FlexListColumn<OrderFragment> = {
      id: 'images',
      label: 'Images',
      get: (row: OrderFragment) => {
         const imageUrls: string[] = [];
         for (const line of row.lines || []) {
            const firstImage = line?.stockItem?.imageUrls?.[0];
            if (firstImage) {
               imageUrls.push(firstImage);
            }
         }
         return imageUrls;
      },
      render: (imageUrls: string[], row) => {
         const itemCount = row?.lines?.length || 0;
         return <Thumbnails maxImages={3} size={45} imageUrls={imageUrls} legend={String(itemCount)}/>;
      },
      width: 5
   };
   const brandColumn: FlexListColumn<OrderFragment> = {
      id: 'brand',
      label: 'Brand',
      field: 'shopId',
      width: 10,
      align: 'left',
      get: (row) => {
         return row.shop?.name
      },
      ellipsis: true
   };
   const allColumns: FlexListColumn<OrderFragment>[] = [{
      id: 'orderDate',
      label: 'Date',
      get: (row: OrderFragment) => {
         if (row.orderDate) {
            return new Date(row.orderDate).getTime()
         } else {
            return 0;
         }
      },
      render: (date, row) => {
         if (date) {
            return DateFormat.toPresent(new Date(date));
         } else {
            return "-"
         }
      },
      ellipsis: true,
      width: 10
   }, {
      id: 'consumer',
      label: 'Customer',
      get: (order) => {
         return order.consumer.mail
      },
      render: (name, order) => {
         return <div>
            <div>{order?.consumer.name}</div>
            <div>{order?.consumer.mail}</div>
         </div>
      },
      ellipsis: true,
      width: 15
   }, {
      id: 'orderUrl',
      label: 'Order',
      field: 'orderUrl',
      render: (url, row) => {
         if (url) {
            return <a onClick={(e) => {
               e.stopPropagation()
            }} href={url} target={"_blank"} rel={"noreferrer"}>{row?.orderName}</a>
         } else {
            if(row?.orderName) {
               return row.orderName
            } else {
               return null;
            }
         }
      },
      ellipsis: true,
      width: 8
   }, {
      id: 'fulfillment',
      label: 'Fulfillment',
      field: 'fulState',
      width: 8,
      ellipsis: true
   }, {
      id: 'payment',
      label: 'Payment',
      field: 'payState',
      width: 8,
      ellipsis: true
   }, {
      id: 'orderState',
      label: 'Order package',
      field: 'trackingOrderState',
      width: 8,
      ellipsis: true
   }, {
      id: 'returnState',
      label: 'Return package',
      field: 'trackingReturnState',
      width: 8,
      ellipsis: true
   }, {
      id: 'items',
      label: "Items",
      get: (row) => {
         return row.lines
      },
      render: (lines: Order2Item[], row) => {
         const returned = lines.some(line => line.returned);
         const cancelled = lines.some(line => line.cancelled);
         return <Grid container>
            <Grid item xs={3}>
               {lines.length} items
            </Grid>
            <Grid item xs={9}>
               {returned &&
                  <Alert icon={false} variant={"filled"} style={{paddingTop: '0', paddingBottom: '0'}}
                         color={"warning"}>Some items returned</Alert>}
               {cancelled &&
                  <Alert icon={false} variant={"filled"} style={{paddingTop: '0', paddingBottom: '0'}}
                         color={"error"}>Some items cancelled</Alert>}
            </Grid>
         </Grid>
      },
      width: 20
   }];

   let listColumns: FlexListColumn<OrderFragment>[] = [];
   if (!shopId && !brandHouseId) {
      listColumns = [imagesColumn, brandColumn, ...allColumns]
   } else {
      listColumns = [imagesColumn, ...allColumns]
   }

   const commands: RangeCommand<OrderFragment>[] = [{
      id: 'export',
      title: 'Export to excel',
      icon: FileDownload,
      execute: async (rowIds) => {
         setExportIds(rowIds || [])
      }
   }]

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter: any) => {
      const effectiveFilter: FilterFindManyOrder2Input = {
         ...filter,
         _operators: {
            silenced: {ne: true}
         }
      }
      if (shopId) {
         effectiveFilter._operators = {
            ...effectiveFilter._operators,
            shopId: {
               in: [shopId]
            }
         }
      }

      if (brandHouseId) {
         effectiveFilter._operators = {
            ...effectiveFilter._operators,
            brandHouseId: {
               in: [brandHouseId]
            }
         }
      }

      const {data} = await fetchMore({
         variables: {
            page: page,
            sort: sort as SortFindManyOrder2Input || SortFindManyOrder2Input._ID_DESC,
            perPage: perPage,
            search: search,
            filter: effectiveFilter,
            viewId: viewId
         }
      });
      return {
         data: data?.OrderPagination?.items || [],
         totalCount: data?.OrderPagination?.count || 0
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fetchMore, viewId]);

   const onSelectAll = useCallback(async (filter?: FilterFindManyOrder2Input): Promise<string[]> => {
      const effectiveFilter: FilterFindManyOrder2Input = {
         ...filter,
         _operators: {
            silenced: {ne: true}
         }
      }
      if (shopId) {
         effectiveFilter._operators = {
            ...effectiveFilter._operators,
            shopId: {
               in: [shopId]
            }
         }
      }

      if (brandHouseId) {
         effectiveFilter._operators = {
            ...effectiveFilter._operators,
            brandHouseId: {
               in: [brandHouseId]
            }
         }
      }

      const {data} = await fetchAllIds({
         variables: {
            filter: effectiveFilter,
            sort: SortFindManyOrder2Input._ID_DESC,
            viewId: viewId
         }
      });
      return data?.OrderPagination?.items?.map(item => item._id) || [];
   }, [fetchAllIds, shopId, brandHouseId, viewId]);

   return <><RangeList globalId={listId}
                      columns={listColumns}
                      filters={hideHeader ? undefined : filterSpec}
                      itemsPerPage={PAGE_SIZE}
                      sorts={sortSpec}
                      tabs={hideHeader ? undefined : tabsSpec}
                      selections={true}
                      commands={[...commands, ...(extraCommands || [])]}
                      onSelectRow={onSelectOrder}
                      onRequestData={onRequestData}
                      onSelectAll={onSelectAll}
                       options={{
                          itemHeight: 55
                       }}
   />
      {exportIds.length > 0 && <OrdersExport language={"da"} currency={"DKK"} ids={exportIds}/>}
   </>
}
